import { DateTime } from 'luxon';
import flatten from 'lodash/flatten';
import isEmpty from 'lodash/isEmpty';
import { PATHS, DEVICE_STATUSES } from '~/utils/constants';
import { STATUS_COLORS } from '~/theme/types/palette';
import type { IConnectivityWidgetStore } from '.';

// Both states use the shared status tokens so the timeline matches the status
// dots used elsewhere: blue for online, grey for offline.
const ONLINE_COLOR = STATUS_COLORS[DEVICE_STATUSES.ONLINE];
const OFFLINE_COLOR = STATUS_COLORS[DEVICE_STATUSES.OFFLINE];

type ConnectivitySegment = {
  x: number;
  x2: number;
  y: number;
  nodeId: string;
  online: boolean;
  color: string;
};

export default (self: IConnectivityWidgetStore) => {
  function getSegmentsByNode(): ConnectivitySegment[][] {
    const now = DateTime.now().toMillis();
    return (self.nodeId || []).map((nodeId, index) => {
      const dataPoint = self.nodes?.getById(nodeId)?.getDataPointByPath(PATHS.ONLINE);
      const measurements = dataPoint?._measurements ?? [];
      return measurements.map(([ts, online]: [number, boolean], i: number) => ({
        x: ts,
        x2: i < measurements.length - 1 ? measurements[i + 1][0] : now,
        y: index,
        nodeId,
        online: Boolean(online),
        color: online ? ONLINE_COLOR : OFFLINE_COLOR
      }));
    });
  }
  return {
    get nodeId() {
      if (isEmpty(self.node_id)) {
        return [];
      }
      return Array.isArray(self.node_id) ? self.node_id : [self.node_id];
    },
    get chartData() {
      return flatten(getSegmentsByNode());
    },
    get chartCategories() {
      return self.nodeId;
    },
    // nodeId -> percentage of the observed span the device was online (0..100).
    get uptimeByNodeId(): Record<string, number> {
      const entries = getSegmentsByNode()
        .filter((segments) => !isEmpty(segments))
        .map((segments) => {
          const span = segments[segments.length - 1].x2 - segments[0].x;
          const onlineMs = segments.reduce((sum, { x, x2, online }) => (online ? sum + (x2 - x) : sum), 0);
          return [segments[0].nodeId, span > 0 ? (onlineMs / span) * 100 : 0] as const;
        });
      return Object.fromEntries(entries);
    },
    get title() {
      return isEmpty(self.name) ? self.nodeId.map((id) => self.nodes?.getById(id)?.presentName).join(' | ') : self.name;
    }
  };
};
